import React, { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { signup } from '../lib/auth'
import api from '../lib/api'
import GuestHeader from '../components/GuestHeader'

export default function RegisterPage() {
  const navigate = useNavigate()
  const { plan } = useParams()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [confirmationSent, setConfirmationSent] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const isPremium = plan === 'premium'

  const isFieldMissing = (val) => submitted && !val

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitted(true)
    setError(null)

    if (!name || !email || !password || !passwordConfirmation) return

    if (password !== passwordConfirmation) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)
    try {
      const data = await signup(name, email, password, passwordConfirmation)
      if (typeof gtag === 'function') {
        gtag('event', 'conversion', { send_to: `${window.GOOGLE_ADS_CONVERSION_ID}` })
      }
      if (data.confirmation_required) {
        setConfirmationSent(true)
      } else {
        // Registration successful, user is now logged in
        if (isPremium) {
          // Redirect to Stripe checkout for premium plan
          try {
            const response = await api.post('/billing/checkout', { plan: 'monthly' })
            window.location.href = response.data.url
          } catch (checkoutErr) {
            setError('Account created, but failed to start checkout. Please upgrade from your account page.')
            setTimeout(() => navigate('/dashboard'), 3000)
          }
        } else {
          // Free plan - go straight to dashboard
          navigate('/dashboard')
        }
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-paper">
      <GuestHeader />
      <div className="flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          <div className="bg-surface border-[1.5px] border-ink p-6">
            {plan && (
              <div className="mb-4 flex justify-center">
                <div className="px-3 py-1 bg-accent/10 rounded-full">
                  <span className="text-sm font-medium text-accent capitalize">{plan} Plan</span>
                </div>
              </div>
            )}
            {confirmationSent ? (
              <div>
                <h2 className="text-xl font-extrabold text-ink mb-4">Check Your Email</h2>
              <p className="text-sm text-gray-600 mb-4">
                We've sent a confirmation link to <span className="font-medium">{email}</span>. Please check your inbox and click the link to verify your account.
              </p>
              <Link
                to="/login"
                className="block w-full py-2 px-4 text-sm font-medium text-center text-white bg-accent rounded-md hover:bg-accent-dark"
              >
                Go to Sign in
              </Link>
            </div>
          ) : (
            <div>
              <h2 className="text-xl font-extrabold text-ink mb-4">Register</h2>

              {error && (
                <div className="text-sm text-accent-dark bg-accent/10 border border-accent/40 p-3 mb-4">{error}</div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={`w-full border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-accent focus:border-accent ${isFieldMissing(name) ? 'border-accent' : ''}`}
                  />
                  {isFieldMissing(name) && <p className="text-xs text-accent-dark mt-1">Required</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-accent focus:border-accent ${isFieldMissing(email) ? 'border-accent' : ''}`}
                  />
                  {isFieldMissing(email) && <p className="text-xs text-accent-dark mt-1">Required</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={6}
                    className={`w-full border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-accent focus:border-accent ${isFieldMissing(password) ? 'border-accent' : ''}`}
                  />
                  {isFieldMissing(password) && <p className="text-xs text-accent-dark mt-1">Required</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    value={passwordConfirmation}
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                    minLength={6}
                    className={`w-full border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-accent focus:border-accent ${isFieldMissing(passwordConfirmation) ? 'border-accent' : ''}`}
                  />
                  {isFieldMissing(passwordConfirmation) && <p className="text-xs text-accent-dark mt-1">Required</p>}
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2 px-4 text-sm font-bold text-white bg-accent rounded-md hover:bg-accent-dark disabled:opacity-50"
                >
                  {loading ? 'Creating account...' : 'Create account'}
                </button>
              </form>

              <p className="mt-4 text-sm text-center text-gray-500">
                Already have an account?{' '}
                <Link to="/login" className="text-accent hover:text-accent-dark">
                  Sign in
                </Link>
              </p>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  )
}
